// Колоните на „По артикули" (справка `assortment`).
//
// Id-тата съвпадат едно към едно с `OrderedItemsSortBy` — така сортирането се
// вдига до BE-то без таблица за превод. „Дял" няма сървърен ключ и затова не
// се сортира; редът ѝ и без това повтаря „Оборот".
//
// Филтрите работят в СЪРВЪРЕН режим: чеклистите носят изрични `options` от
// менюто, защото фасетите виждат само текущата страница и списъкът би лъгал.
// Стойността на опцията е id-то (него чака BE-то), етикетът е името.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { formatPercent } from "../../../../components/dataTable";
import type {
  BarCellValue,
  ChecklistOption,
  ChipCellValue,
  TextCellValue,
} from "../../../../components/dataTable";
import type { OrderedItem } from "../../../../api/reports";

/**
 * Ред с ВЕЧЕ РАЗРЕШЕНИ имена и числа. Етикетът на вида и делът не се вадят в
 * `accessorFn`: TanStack кешира стойността на клетката за живота на `data`, а
 * React Query пази същата референция при непроменен отговор — стойност,
 * зависеща от нещо извън реда (превод, оборот за периода), иначе замръзва.
 */
export type NamedOrderedItem = OrderedItem & {
  /** Асортимент / Рецепта / Добавка — според режима „със склад". */
  kindLabel: string;
  quantity: number;
  avgPrice: number;
  revenue: number;
  /** Дял от оборота за периода, в проценти. */
  sharePercent: number;
};

const columnHelper = createColumnHelper<NamedOrderedItem>();

type Options = {
  categoryOptions: ChecklistOption[];
  stationOptions: ChecklistOption[];
  kindOptions: ChecklistOption[];
};

export const useItemsColumns = ({
  categoryOptions,
  stationOptions,
  kindOptions,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      // Един и същ продаваем може да е на два реда — като продукт и като
      // добавка, с различна цена. Вторият ред на клетката казва кой кой е,
      // за да не изглеждат като дубликат; при продуктите остава празен.
      columnHelper.accessor(
        (row): TextCellValue => ({
          primary: row.name,
          secondary: row.is_addon ? row.kindLabel : null,
          // Продаденото от обедно меню — етикет до името (редът е отделен от
          // същия продукт на основната цена)
          tag: row.is_lunch ? { tone: "brand", label: t("reportsPage.lunchTag") } : null,
        }),
        {
          id: "name",
          header: t("reportsPage.productName"),
          size: 240,
          meta: {
            cell: { kind: "text", lines: 2 },
            alwaysVisible: true,
            excel: { format: "text" },
          },
        },
      ),

      columnHelper.accessor((row) => row.category_name ?? "", {
        id: "category_name",
        header: t("reportsPage.category"),
        size: 168,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist", options: categoryOptions },
          excel: { format: "text" },
        },
      }),

      columnHelper.accessor((row) => row.station_name, {
        id: "station_name",
        header: t("reportsPage.station"),
        size: 152,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist", options: stationOptions },
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      // Изгасена по подразбиране: екранът е за артикули, не за видове. Стои в
      // панела „Колони" заради филтъра — той замени падащото меню „Тип" от
      // стария екран и е единственият път до „само добавките".
      columnHelper.accessor(
        (row): ChipCellValue => ({
          tone: row.is_addon ? "brand" : row.is_lunch ? "info" : "neutral",
          label: row.kindLabel,
        }),
        {
          id: "is_addon",
          header: t("reportsPage.assortmentColKind"),
          size: 124,
          meta: {
            cell: { kind: "chip" },
            filter: { kind: "checklist", options: kindOptions },
            defaultHidden: true,
            excel: { format: "text" },
          },
        },
      ),

      // БЕЗ `decimals` — теглените артикули идват с дробно количество и
      // закръглянето до цяло би показало 0.750 кг като „1".
      columnHelper.accessor((row) => row.quantity, {
        id: "count",
        header: t("reportsPage.itemsColCount"),
        size: 104,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      columnHelper.accessor((row) => row.avgPrice, {
        id: "avg_price",
        header: t("reportsPage.itemsColPrice"),
        size: 112,
        meta: {
          cell: { kind: "money" },
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),

      columnHelper.accessor((row) => row.revenue, {
        id: "total_sum",
        header: t("reportsPage.itemsColRevenue"),
        size: 124,
        meta: {
          cell: { kind: "money", strong: true },
          total: "sum",
          alwaysVisible: true,
          excel: { format: "money" },
        },
      }),

      columnHelper.accessor(
        (row): BarCellValue => ({
          value: row.sharePercent,
          max: 100,
          label: formatPercent(row.sharePercent, 1),
        }),
        {
          id: "share",
          header: t("reportsPage.itemsColShare"),
          size: 128,
          // Няма сървърен ключ за дял, а подредбата по него е същата като по
          // „Оборот" — стрелка, която не мести нищо, е по-лоша от липсваща.
          enableSorting: false,
          meta: { cell: { kind: "bar" }, hideBelow: "narrow" },
        },
      ),
    ],
    [t, categoryOptions, stationOptions, kindOptions],
  );
};
